import { useEffect } from 'react';

/**
 * Google Fonts CSS API endpoint for Inter, static weights 400–700 with
 * `display=swap`.
 *
 * We load Inter through the official Google Fonts API rather than hardcoded
 * `fonts.gstatic.com` woff2 URLs. Google resolves the concrete subset files at
 * request time, and — crucially — if the request is blocked or hung (e.g. by
 * Safari privacy tooling) `display=swap` lets text fall back to the system font
 * chain declared in `@xemplo/root-style` instead of rendering nothing.
 */
const GOOGLE_FONTS_STYLESHEET =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';

type FontLink = {
  rel: string;
  href: string;
  /** Emits the bare `crossorigin` attribute required for the gstatic preconnect. */
  crossOrigin?: boolean;
};

const FONT_LINKS: FontLink[] = [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: true },
  { rel: 'stylesheet', href: GOOGLE_FONTS_STYLESHEET },
];

/**
 * Appends a `<link>` to `document.head`, skipping it if a link with the same
 * `rel` and `href` already exists. This keeps injection idempotent across
 * StrictMode's double-invoked effects and multiple micro-frontends that each
 * mount their own `AppBootstrap` into the same document.
 */
function ensureHeadLink({ rel, href, crossOrigin }: FontLink): void {
  const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
  if (existing) return;

  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (crossOrigin) link.setAttribute('crossorigin', '');
  document.head.appendChild(link);
}

/**
 * Injects the Google Fonts preconnect and Inter stylesheet links into the
 * document head on mount. Loading lives here (app bootstrap) rather than in
 * `RootStyle` so that `root-style` stays purely CSS ahead of the CSS-modules
 * migration.
 */
export function useGlobalFont(): void {
  useEffect(() => {
    FONT_LINKS.forEach(ensureHeadLink);
  }, []);
}
